/**
 * Modal d'édition/création de produit
 * Formulaire complet avec tous les champs du DTO
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { prepareImageForUpload } from '../utils/imagePrep';
import axios from 'axios';
import { api, type ProductImage } from '../services/api';
import { posApi } from '../api/pos.api';
import type { Product } from '../types/pos.types';
import type { ProductLot } from '../types/lots.types';
import { useTenant } from '../contexts/TenantContext';
import { PrintShelfLabelsModal } from './labels/PrintShelfLabelsModal';
import WebcamCaptureModal from './ui/WebcamCaptureModal';
import ImageLightbox from './ui/ImageLightbox';
import ImageEditModal from './ui/ImageEditModal';
import { logger } from '../utils/logger';
import DLCInput from './ui/DLCInput';
import ProductCountModal from './ProductCountModal';
import BilanSeriesModal from './pos/BilanSeriesModal';
import RemiseLotsModal from './remise/RemiseLotsModal';
import PriceInput from './ui/PriceInput';
import { useScale } from '../hooks/useScale';
import { suggestSupplier } from '../utils/supplierMatch';

// La fiche n'affiche que les lots portant du stock. Les lots ACTIVE à 0/0 —
// pour l'essentiel les lots vides que l'inventaire crée en masse (~357 en base,
// cf. createSession) — n'offrent aucune action ici : rien à transférer, DLC sans
// marchandise. Filtre d'affichage uniquement : les lots restent en base et
// l'inventaire continue de les utiliser.
const withStock = (lots: ProductLot[]) =>
  lots.filter((l) => l.stock_backroom > 0 || l.stock_display > 0);

interface ProductEditModalProps {
  product?: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface Category {
  id: string;
  name: string;
  path?: string;
}

export default function ProductEditModal({ product, isOpen, onClose, onSave }: ProductEditModalProps) {
  const { tenant, loading: tenantLoading } = useTenant();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [depositProducts, setDepositProducts] = useState<Product[]>([]);
  const [suppliers, setSuppliers] = useState<string[]>([]); // liste dédupliquée (combobox)

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    barcode: '',
    description: '',
    is_weighted: false,
    unit: 'pièce',
    tax_class: '6%',
    image_url: '',
    active: true,
    supplier_name: '',
    producer_name: '',
    origin_country: '',
    stock_quantity: 0,
    category_id: '',
    bio: false,
    vegan: false,
    print_dlc_on_ticket: false,
    ne_se_coupe_pas: false,
    is_deposit: false,
    deposit_product_id: '',
    deposit_qty_per_unit: '1',
    organic_certifier: '',
    expiry_date: '',
    purchase_price_cents: 0,
    suggested_price_cents: 0,
    gross_weight_grams: 0,
    variable_weight: false,
    sell_by_pieces: false,
    ecom_pieces_only: false,
    ecom_pieces_first: false,
    avg_weight_per_piece_grams: null as number | null,
    pack_size: null as number | null,
    preorder_enabled: false,
    preorder_type_id: '' as string,
    /** Jours de livraison fournisseur propres à ce produit (vide = ceux du type). */
    production_days: [] as number[],
    /** Semaines du mois livrées (1..5). Vide = toutes. Ex. seigle : 1er et 3e vendredis. */
    delivery_weeks_of_month: [] as number[],
    availability_days: [] as ('FRIDAY' | 'SATURDAY')[],
    restock_alert_threshold: null as number | null,
    order_threshold: null as number | null,
    // Rayon plein : ce qui tient sur l'étal. Déduit chaque nuit des mises en
    // rayon (on s'arrête de ranger quand c'est plein), corrigeable ici et
    // depuis la ligne de réappro. Une saisie n'est jamais réécrite.
    shelf_capacity: null as number | null,
  });

  // Coquille de frappe → propose le fournisseur connu le plus proche, sans jamais
  // fusionner d'office (l'opérateur confirme). Les variantes d'orthographe pures
  // sont déjà normalisées côté backend, donc pas de chip dans ce cas.
  const supplierSuggestion = useMemo(
    () => suggestSupplier(formData.supplier_name || '', suppliers),
    [formData.supplier_name, suppliers],
  );

  // Prix séparé (géré via endpoint dédié)
  const [price, setPrice] = useState(0);

  // Types de précommande (Pain, Viande, Poisson...) — chargés dynamiquement (extensible)
  const [preorderTypes, setPreorderTypes] = useState<Array<{ id: string; name: string; pickup_mode?: string; pickup_days?: number[]; prep_options?: string[] }>>([]);
  useEffect(() => {
    api.get('/preorders/types').then((t: any) => setPreorderTypes(Array.isArray(t) ? t : [])).catch(() => {});
  }, []);
  const WEEKDAYS_SHORT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const selType = preorderTypes.find((t) => t.id === formData.preorder_type_id);

  // Prix d'achat et conseillé en euros (pour éviter les problèmes de saisie avec les centimes)
  const [purchasePrice, setPurchasePrice] = useState('');
  const [suggestedPrice, setSuggestedPrice] = useState('');

  // État pour le certificateur personnalisé
  const [customCertifier, setCustomCertifier] = useState('');
  const [showCustomCertifier, setShowCustomCertifier] = useState(false);

  // État pour la gestion des photos
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [editingImage, setEditingImage] = useState<ProductImage | null>(null);
  // Plusieurs gestes de cette fiche écrivent IMMÉDIATEMENT, sans passer par
  // « Enregistrer » : photos, DLC d'un lot, scission, comptage. La liste du
  // parent, elle, ne recharge que sur onSave — d'où ce drapeau, levé par ces
  // gestes et honoré à la fermeture. Sans lui, on compte un produit, on ferme,
  // et la page Stocks affiche encore l'ancien chiffre (signalé le 03/09/2026).
  const changedOutsideSaveRef = useRef(false);
  const [expandedLabelText, setExpandedLabelText] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [webcamImageType, setWebcamImageType] = useState<'PRODUCT' | 'LABEL'>('PRODUCT');

  // État pour le modal d'impression d'étiquettes
  const [showLabelPrintModal, setShowLabelPrintModal] = useState(false);
  /**
   * Annonce de nouveauté, depuis la fiche.
   *
   * Le geste normal est au rangement, l'article en main. Celui-ci est le chemin
   * de rattrapage — « on a oublié hier » — et le seul endroit commode pour
   * marquer « De retour » sur un produit qu'on ne range pas aujourd'hui.
   */
  const [nouveaute, setNouveaute] = useState<{ mot: string } | null>(null);
  const [nouveauteEnCours, setNouveauteEnCours] = useState(false);

  const annoncerNouveaute = async (kind: 'NEW' | 'BACK') => {
    const id = product?.id || selectedExistingProduct?.id;
    if (!id) return;
    try {
      setNouveauteEnCours(true);
      const res: any = await api.patch(`/products/${id}/novelty`, { kind });
      setNouveaute({ mot: res?.novelty_word || (kind === 'BACK' ? 'De retour' : 'Nouveau') });
    } catch (err) {
      alert(err instanceof Error ? err.message : "Échec de l'annonce");
    } finally {
      setNouveauteEnCours(false);
    }
  };

  const retirerNouveaute = async () => {
    const id = product?.id || selectedExistingProduct?.id;
    if (!id) return;
    try {
      setNouveauteEnCours(true);
      await api.delete(`/products/${id}/novelty`);
      setNouveaute(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Échec du retrait');
    } finally {
      setNouveauteEnCours(false);
    }
  };


  // Balance (connexion directe via useScale)
  const {
    connected: scaleConnected,
    connecting: scaleConnecting,
    currentWeight: scaleWeight,
    connect: scaleConnect,
    disconnect: scaleDisconnect,
  } = useScale();
  const scaleWeightGrams = scaleWeight?.weight ?? 0;

  // État pour la recherche de produit existant (mode création)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedExistingProduct, setSelectedExistingProduct] = useState<Product | null>(null);

  // État pour les lots et la mise en rayon
  const [productLots, setProductLots] = useState<ProductLot[]>([]);
  // Lots illisibles (session expirée, réseau) : ne JAMAIS afficher « 0 en stock »
  // à la place — on pourrait « corriger » un stock réel vers zéro (14/10/2026).
  const [lotsIllisibles, setLotsIllisibles] = useState(false);
  /**
   * Dernier lot RECU, epuise compris. La fiche ne montre que les lots qui
   * portent du stock — a raison, sinon les lots vides de l'inventaire
   * l'encombrent. Mais quand le dernier est solde, la DLC, la date de
   * reception et le fournisseur disparaissent : impossible de repondre a
   * « quelle etait la DLC du dernier Skyr ? » (demande de Philippe, 21/09/2026).
   */
  const [dernierLot, setDernierLot] = useState<any | null>(null);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [moveQuantities, setMoveQuantities] = useState<Record<string, number>>({});
  const [backroomQuantities, setBackroomQuantities] = useState<Record<string, number>>({});
  const [movingLotId, setMovingLotId] = useState<string | null>(null);

  // Flag pour savoir si l'utilisateur a manuellement modifié le stock
  // Évite de renvoyer stock_quantity au backend lors d'une simple édition produit
  // (sinon le total backroom+display est réécrit en backroom, doublant le stock après mise en rayon)
  const [stockDirty, setStockDirty] = useState(false);
  // Comptage du produit (mini-inventaire) : SEUL chemin d'ajustement du stock
  // quand un lot actif existe — l'édition directe est ignorée par le backend.
  const [showCount, setShowCount] = useState(false);
  const [showSeries, setShowSeries] = useState(false);
  // Remise anti-gaspi par lot, posée depuis la fiche (porte d'entrée du quotidien).
  const [showRemiseLots, setShowRemiseLots] = useState(false);
  // Même logique que stockDirty pour la DLC : le formulaire précharge expiry_date
  // depuis la fiche, mais le champ VISIBLE édite le lot (voir DLCInput plus bas).
  // Renvoyer cette valeur préchargée à l'enregistrement réécrasait une DLC corrigée
  // entre-temps par un autre écran (réappro « À vérifier ») — incident du 04/08/2026
  // sur Chips Tortilla Nature. On ne l'envoie donc que si l'utilisateur l'a saisie.
  const [expiryDirty, setExpiryDirty] = useState(false);
  // Même logique pour les prix d'achat et conseillé (prévu le 08/09/2026) : le formulaire
  // les précharge depuis la fiche et `sanitizeProductData` les renvoie toujours. Une fiche
  // ouverte avec un objet incomplet (prix absent → 0), ou dont le prix a été changé
  // ailleurs entre-temps, réécrivait au premier enregistrement le prix d'achat de
  // référence de l'écran Marges. On ne les envoie que si l'utilisateur les a touchés.
  const [priceDirty, setPriceDirty] = useState(false);

  // Édition de la DLC d'un lot PRÉCIS (clic sur le lot dans la liste). Le champ DLC
  // principal de la fiche n'édite que le lot FEFO (primaryLot) — avec plusieurs lots,
  // les autres étaient inaccessibles depuis la fiche (garde-fou du 04/08 : la fiche
  // ne propage jamais une DLC sur plusieurs lots).
  const [dlcEditLotId, setDlcEditLotId] = useState<string | null>(null);
  const [dlcSavingLotId, setDlcSavingLotId] = useState<string | null>(null);

  const handleLotDlcChange = async (lotId: string, isoDate: string) => {
    try {
      setDlcSavingLotId(lotId);
      await posApi.updateLot(lotId, { expiry_date: isoDate || null });
      changedOutsideSaveRef.current = true;
      await loadProductLots();
      // Champ vidé = DLC retirée, mais on laisse l'éditeur ouvert : vider est le
      // plus souvent la première moitié d'un « je retape la bonne date ».
      if (isoDate) setDlcEditLotId(null);
    } catch (err) {
      logger.error('Erreur mise à jour DLC lot:', err);
      alert('Erreur lors de la mise à jour de la DLC');
    } finally {
      setDlcSavingLotId(null);
    }
  };

  // Scission d'un lot : colis d'une même livraison portant deux DLC.
  const [splitTarget, setSplitTarget] = useState<ProductLot | null>(null);
  const [splitQtyBackroom, setSplitQtyBackroom] = useState('');
  const [splitQtyDisplay, setSplitQtyDisplay] = useState('');
  const [splitDlc, setSplitDlc] = useState('');
  const [splitting, setSplitting] = useState(false);

  const confirmSplit = async () => {
    if (!splitTarget) return;
    const qb = parseFloat((splitQtyBackroom || '0').replace(',', '.')) || 0;
    const qd = parseFloat((splitQtyDisplay || '0').replace(',', '.')) || 0;
    if (qb <= 0 && qd <= 0) { alert("Indiquez la quantité qui porte l'autre DLC."); return; }
    if (!splitDlc) { alert('Indiquez la DLC du nouveau lot.'); return; }
    try {
      setSplitting(true);
      const r = await posApi.splitLot(splitTarget.id, {
        quantity_backroom: qb || undefined,
        quantity_display: qd || undefined,
        expiry_date: splitDlc,
      });
      changedOutsideSaveRef.current = true;
      setSplitTarget(null);
      await loadProductLots();
      alert(`Lot scindé : ${r.new_lot?.lot_number ?? ''} créé.`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la scission');
    } finally {
      setSplitting(false);
    }
  };

  // DLC du lot principal (pour synchronisation avec la section traçabilité)
  const primaryLot = productLots.length > 0
    ? productLots.reduce((best, lot) => {
        // Priorité aux lots avec DLC, triés par date croissante (FEFO)
        if (!best) return lot;
        if (!lot.expiry_date && best.expiry_date) return best;
        if (lot.expiry_date && !best.expiry_date) return lot;
        if (lot.expiry_date && best.expiry_date) {
          return new Date(lot.expiry_date) < new Date(best.expiry_date) ? lot : best;
        }
        return best;
      }, null as ProductLot | null)
    : null;

  // Liste des certificateurs bio par pays
  const certifiers = {
    belgium: [
      'Certisys',
      'Quality Partner',
      'TÜV NORD Integra',
    ],
    france: [
      'Ecocert',
      'Bureau Veritas',
      'Certipaq Bio',
      'Agrocert',
      'Bureau Alpes Contrôles',
      'Qualisud',
    ],
    italy: [
      'ICEA',
      'CCPB',
      'Bioagricert',
      'Suolo e Salute',
      'IMC',
    ],
  };

  useEffect(() => {
    if (!tenantLoading && tenant && isOpen) {
      loadCategories();
      loadDepositProducts();
      loadSuppliers();
    }
  }, [tenant, tenantLoading, isOpen]);

  // Charger les lots du produit en mode édition
  useEffect(() => {
    if (product?.id && isOpen) {
      loadProductLots();
    }
  }, [product?.id, isOpen]);

  /**
   * État de l'annonce de nouveauté à l'ouverture.
   *
   * Il était posé UNIQUEMENT dans le chemin « produit existant retrouvé par
   * recherche » : ouvert depuis la liste des produits — le cas normal — la fiche
   * ignorait qu'un article était annoncé et proposait de l'annoncer encore, sans
   * jamais offrir de le retirer (signalé le 10/09/2026, « Bonbon le gueule de
   * bois »). L'état est relu ici, à chaque ouverture : la liste peut dater d'un
   * chargement antérieur à l'annonce.
   */
  useEffect(() => {
    if (!isOpen || !product?.id) {
      setNouveaute(null);
      return;
    }
    setNouveaute(
      (product as any).is_novelty ? { mot: (product as any).novelty_word || 'Nouveau' } : null,
    );
    let vivant = true;
    api
      .getProduct(product.id)
      .then((frais: any) => {
        if (!vivant) return;
        setNouveaute(frais?.is_novelty ? { mot: frais.novelty_word || 'Nouveau' } : null);
      })
      .catch(() => {
        /* la fiche reste utilisable : on garde ce que la liste disait */
      });
    return () => {
      vivant = false;
    };
  }, [product?.id, isOpen]);

  // Charger les images multi du produit en mode édition
  useEffect(() => {
    changedOutsideSaveRef.current = false;
    if (product?.id && isOpen) {
      api.getProductImages(product.id)
        .then(images => setProductImages(images))
        .catch(err => logger.warn('[ProductEditModal] Erreur chargement images:', err));
    } else {
      setProductImages([]);
    }
  }, [product?.id, isOpen]);

  /** Fermeture : si quoi que ce soit a été écrit sans « Enregistrer », la liste du parent doit recharger. */
  const handleCloseModal = () => {
    if (changedOutsideSaveRef.current) {
      changedOutsideSaveRef.current = false;
      onSave();
    }
    onClose();
  };

  // Calcul automatique du prix conseillé TTC quand le prix d'achat change.
  // MÊME RÈGLE QU'À LA RÉCEPTION (alignées le 12/08/2026, à garder synchrones
  // avec DeliveryVerificationTab) : prix d'achat TTC × 1,5, arrondi au 0,05 €
  // SUPÉRIEUR (arrondi commercial belge). Le prix d'achat de la fiche est HT →
  // on le passe en TTC via la classe de TVA avant d'appliquer la marge.
  useEffect(() => {
    if (!purchasePrice || purchasePrice.trim() === '') return;

    const purchasePriceHT = parseFloat(purchasePrice.replace(',', '.'));
    if (isNaN(purchasePriceHT) || purchasePriceHT <= 0) return;

    // TVA selon tax_class (par défaut 6% pour alimentaire)
    let tvaRate = 6;
    if (formData.tax_class === '21%') {
      tvaRate = 21;
    } else if (formData.tax_class === '12%') {
      tvaRate = 12;
    }

    // En cents pour éviter les dérives flottantes
    const purchaseTtcCents = Math.round(purchasePriceHT * (1 + tvaRate / 100) * 100);
    const suggestedCents = Math.ceil((purchaseTtcCents * 1.5) / 5) * 5;

    setSuggestedPrice((suggestedCents / 100).toFixed(2));
  }, [purchasePrice, formData.tax_class]);

  // Recherche de produit existant avec debounce (mode création uniquement)
  useEffect(() => {
    // Ne pas chercher si on est en mode édition
    if (product || selectedExistingProduct) return;

    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await api.suggestProducts(searchQuery, 8);
        setSearchResults(results);
        setShowSearchResults(results.length > 0);
      } catch (error) {
        logger.error('[ProductEditModal] Erreur recherche produits:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, product, selectedExistingProduct]);

  // Fonction pour sélectionner un produit existant et passer en mode édition
  const handleSelectExistingProduct = async (productId: string) => {
    try {
      setLoading(true);
      const fullProduct = await api.getProduct(productId);
      setSelectedExistingProduct(fullProduct);
      // L'annonce de nouveauté n'est servie que tant qu'elle est vraie : son
      // absence VEUT DIRE « pas annoncé », il n'y a rien à faire expirer ici.
      setNouveaute(
        (fullProduct as any)?.is_novelty
          ? { mot: (fullProduct as any).novelty_word || 'Nouveau' }
          : null,
      );
      setShowSearchResults(false);
      setSearchQuery('');

      // Charger les données du produit dans le formulaire
      setFormData({
        name: fullProduct.name || '',
        code: fullProduct.code || '',
        barcode: fullProduct.barcode || '',
        description: fullProduct.description || '',
        is_weighted: fullProduct.is_weighted || false,
        unit: fullProduct.unit || 'pièce',
        tax_class: fullProduct.tax_class || '6%',
        image_url: fullProduct.image_url || '',
        active: fullProduct.active ?? true,
        supplier_name: fullProduct.supplier_name || '',
        producer_name: fullProduct.producer_name || '',
        origin_country: fullProduct.origin_country || '',
        stock_quantity: fullProduct.stock_quantity || 0,
        category_id: fullProduct.category_id || '',
        bio: fullProduct.bio || false,
        vegan: fullProduct.vegan || false,
        print_dlc_on_ticket: (fullProduct as any).print_dlc_on_ticket || false,
        ne_se_coupe_pas: (fullProduct as any).ne_se_coupe_pas || false,
        is_deposit: fullProduct.is_deposit || false,
        deposit_product_id: fullProduct.deposit_product_id || '',
        deposit_qty_per_unit: fullProduct.deposit_qty_per_unit?.toString() || '1',
        organic_certifier: fullProduct.organic_certifier || '',
        expiry_date: fullProduct.expiry_date ? fullProduct.expiry_date.split('T')[0] : '',
        purchase_price_cents: fullProduct.purchase_price_cents || 0,
        suggested_price_cents: fullProduct.suggested_price_cents || 0,
        gross_weight_grams: fullProduct.gross_weight_grams || 0,
        variable_weight: fullProduct.variable_weight || false,
        sell_by_pieces: fullProduct.sell_by_pieces || false,
        ecom_pieces_only: fullProduct.ecom_pieces_only || false,
        ecom_pieces_first: fullProduct.ecom_pieces_first || false,
        avg_weight_per_piece_grams: fullProduct.avg_weight_per_piece_grams ?? null,
        pack_size: fullProduct.pack_size ?? null,
        preorder_enabled: fullProduct.preorder_enabled || false,
        preorder_type_id: fullProduct.preorder_type_id || '',
        production_days: fullProduct.production_days || [],
        delivery_weeks_of_month: fullProduct.delivery_weeks_of_month || [],
        availability_days: fullProduct.availability_days || [],
        restock_alert_threshold: fullProduct.restock_alert_threshold ?? null,
        order_threshold: fullProduct.order_threshold ?? null,
        shelf_capacity: fullProduct.shelf_capacity ?? null,
      });
      setPrice((fullProduct.prices?.[0]?.amount_cents || 0) / 100);
      setPurchasePrice(fullProduct.purchase_price_cents ? (fullProduct.purchase_price_cents / 100).toString() : '');
      setSuggestedPrice(fullProduct.suggested_price_cents ? (fullProduct.suggested_price_cents / 100).toString() : '');
      setStockDirty(false);
      setExpiryDirty(false);
      setPriceDirty(false);

      // Charger les lots du produit
      if (fullProduct.id) {
        let lots: ProductLot[] = [];
        try {
          lots = withStock(await posApi.getProductLots(fullProduct.id));
          setLotsIllisibles(false);
        } catch {
          setLotsIllisibles(true);
        }
        setProductLots(lots);
        // Best effort : une fiche doit s'ouvrir meme si cette lecture echoue.
        posApi
          .getLastReceivedLot(fullProduct.id)
          .then(setDernierLot)
          .catch(() => setDernierLot(null));
        const initialMoveQuantities: Record<string, number> = {};
        const initialBackroomQuantities: Record<string, number> = {};
        lots.forEach((lot: ProductLot) => {
          initialMoveQuantities[lot.id] = lot.stock_backroom;
          initialBackroomQuantities[lot.id] = lot.stock_display;
        });
        setMoveQuantities(initialMoveQuantities);
        setBackroomQuantities(initialBackroomQuantities);
      }
    } catch (error) {
      logger.error('[ProductEditModal] Erreur chargement produit:', error);
      alert('Erreur lors du chargement du produit');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour revenir au mode création
  const handleBackToCreate = () => {
    setSelectedExistingProduct(null);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setProductLots([]);
    // Reset form
    setFormData({
      name: '',
      code: '',
      barcode: '',
      description: '',
      is_weighted: false,
      unit: 'pièce',
      tax_class: '6%',
      image_url: '',
      active: true,
      supplier_name: '',
      producer_name: '',
      origin_country: '',
      stock_quantity: 0,
      category_id: '',
      bio: false,
      vegan: false,
      print_dlc_on_ticket: false,
      ne_se_coupe_pas: false,
      is_deposit: false,
      deposit_product_id: '',
      deposit_qty_per_unit: '1',
      organic_certifier: '',
      expiry_date: '',
      purchase_price_cents: 0,
      suggested_price_cents: 0,
      gross_weight_grams: 0,
      variable_weight: false,
      sell_by_pieces: false,
      ecom_pieces_only: false,
      ecom_pieces_first: false,
      avg_weight_per_piece_grams: null,
      pack_size: null,
      preorder_enabled: false,
      preorder_type_id: '',
      production_days: [],
      delivery_weeks_of_month: [],
      availability_days: [],
      restock_alert_threshold: null,
      order_threshold: null,
      shelf_capacity: null,
    });
    setPrice(0);
    setPurchasePrice('');
    setSuggestedPrice('');
    setPhotoPreview(null);
    setStockDirty(false);
      setExpiryDirty(false);
      setPriceDirty(false);
  };

  const loadProductLots = async () => {
    if (!product?.id) return;

    try {
      setLotsLoading(true);
      const lots = withStock(await posApi.getProductLots(product.id));
      posApi
        .getLastReceivedLot(product.id)
        .then(setDernierLot)
        .catch(() => setDernierLot(null));
      setProductLots(lots);

      // Initialiser les quantités avec le stock en réserve et en rayon
      const initialMoveQuantities: Record<string, number> = {};
      const initialBackroomQuantities: Record<string, number> = {};
      lots.forEach((lot: ProductLot) => {
        initialMoveQuantities[lot.id] = lot.stock_backroom;
        initialBackroomQuantities[lot.id] = lot.stock_display;
      });
      setMoveQuantities(initialMoveQuantities);
      setBackroomQuantities(initialBackroomQuantities);
    } catch (error) {
      logger.error('Erreur chargement lots:', error);
    } finally {
      setLotsLoading(false);
    }
  };

  const handleMoveToDisplay = async (lotId: string) => {
    const quantity = moveQuantities[lotId] || 0;

    if (quantity <= 0) {
      alert('La quantité doit être supérieure à 0');
      return;
    }

    try {
      setMovingLotId(lotId);
      const result = await posApi.moveToDisplay(lotId, quantity);

      // La mise en rayon réactive le produit côté backend. Synchroniser le state
      // local du formulaire pour qu'un « Enregistrer » ultérieur ne le re-désactive pas
      // (bug 11/06/2026 : Riz Jasmin Blanc remis inactif par un save post-mise-en-rayon).
      if (result?.productActivated) {
        setFormData(prev => ({ ...prev, active: true }));
      }

      // Recharger les lots
      await loadProductLots();

      // Notifier le parent pour rafraîchir les données du produit
      onSave();
    } catch (error: any) {
      logger.error('Erreur mise en rayon:', error);
      alert(error.message || 'Erreur lors de la mise en rayon');
    } finally {
      setMovingLotId(null);
    }
  };

  const handleMoveToBackroom = async (lotId: string) => {
    const quantity = backroomQuantities[lotId] || 0;

    if (quantity <= 0) {
      alert('La quantité doit être supérieure à 0');
      return;
    }

    try {
      setMovingLotId(lotId);
      await posApi.moveToBackroom(lotId, quantity);

      // Recharger les lots
      await loadProductLots();

      // Notifier le parent pour rafraîchir les données du produit
      onSave();
    } catch (error: any) {
      logger.error('Erreur remise en réserve:', error);
      alert(error.message || 'Erreur lors de la remise en réserve');
    } finally {
      setMovingLotId(null);
    }
  };

  // Gérer la prévisualisation de l'image
  useEffect(() => {
    if (formData.image_url) {
      setPhotoPreview(formData.image_url);
    }
  }, [formData.image_url]);

  // Gérer l'affichage du champ certificateur personnalisé
  useEffect(() => {
    if (formData.organic_certifier) {
      const allCertifiers = [...certifiers.belgium, ...certifiers.france, ...certifiers.italy];
      if (!allCertifiers.includes(formData.organic_certifier)) {
        setShowCustomCertifier(true);
        setCustomCertifier(formData.organic_certifier);
      }
    }
  }, [formData.organic_certifier]);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        code: product.code || '',
        barcode: product.barcode || '',
        description: product.description || '',
        is_weighted: product.is_weighted || false,
        unit: product.unit || 'pièce',
        tax_class: product.tax_class || '6%',
        image_url: product.image_url || '',
        active: product.active ?? true,
        supplier_name: product.supplier_name || '',
        producer_name: product.producer_name || '',
        origin_country: product.origin_country || '',
        stock_quantity: product.stock_quantity || 0,
        category_id: product.category_id || '',
        bio: product.bio || false,
        vegan: product.vegan || false,
        print_dlc_on_ticket: (product as any).print_dlc_on_ticket || false,
        ne_se_coupe_pas: (product as any).ne_se_coupe_pas || false,
        is_deposit: product.is_deposit || false,
        deposit_product_id: product.deposit_product_id || '',
        deposit_qty_per_unit: product.deposit_qty_per_unit?.toString() || '1',
        organic_certifier: product.organic_certifier || '',
        expiry_date: product.expiry_date ? product.expiry_date.split('T')[0] : '',
        purchase_price_cents: product.purchase_price_cents || 0,
        suggested_price_cents: product.suggested_price_cents || 0,
        gross_weight_grams: product.gross_weight_grams || 0,
        variable_weight: product.variable_weight || false,
        sell_by_pieces: product.sell_by_pieces || false,
        ecom_pieces_only: product.ecom_pieces_only || false,
        ecom_pieces_first: product.ecom_pieces_first || false,
        avg_weight_per_piece_grams: product.avg_weight_per_piece_grams ?? null,
        pack_size: product.pack_size ?? null,
        preorder_enabled: product.preorder_enabled || false,
        preorder_type_id: (product as any).preorder_type_id || '',
        production_days: (product as any).production_days || [],
        delivery_weeks_of_month: (product as any).delivery_weeks_of_month || [],
        availability_days: product.availability_days || [],
        restock_alert_threshold: product.restock_alert_threshold ?? null,
        order_threshold: product.order_threshold ?? null,
        shelf_capacity: (product as any).shelf_capacity ?? null,
      });
      setPrice((product.prices?.[0]?.amount_cents || 0) / 100);
      setStockDirty(false);
      setExpiryDirty(false);
      setPriceDirty(false);
      // Initialiser les prix d'achat et conseillé
      setPurchasePrice(product.purchase_price_cents ? (product.purchase_price_cents / 100).toString() : '');
      setSuggestedPrice(product.suggested_price_cents ? (product.suggested_price_cents / 100).toString() : '');
    } else {
      // Reset pour création
      setFormData({
        name: '',
        code: '',
        barcode: '',
        description: '',
        is_weighted: false,
        unit: 'pièce',
        tax_class: '6%',
        image_url: '',
        active: true,
        supplier_name: '',
        producer_name: '',
        origin_country: '',
        stock_quantity: 0,
        category_id: '',
        bio: false,
        vegan: false,
      print_dlc_on_ticket: false,
      ne_se_coupe_pas: false,
        is_deposit: false,
        deposit_product_id: '',
        deposit_qty_per_unit: '1',
        organic_certifier: '',
        expiry_date: '',
        purchase_price_cents: 0,
        suggested_price_cents: 0,
        gross_weight_grams: 0,
        variable_weight: false,
        sell_by_pieces: false,
        ecom_pieces_only: false,
        ecom_pieces_first: false,
        avg_weight_per_piece_grams: null,
        pack_size: null,
        preorder_enabled: false,
        preorder_type_id: '',
        production_days: [],
        delivery_weeks_of_month: [],
        availability_days: [],
        restock_alert_threshold: null,
        order_threshold: null,
        shelf_capacity: null,
      });
      setPrice(0);
      setPurchasePrice('');
      setSuggestedPrice('');
      setStockDirty(false);
      setExpiryDirty(false);
      setPriceDirty(false);
    }
  }, [product]);

  const loadCategories = async () => {
    if (!tenant) {
      logger.warn('Tenant pas encore chargé, skip loadCategories');
      return;
    }

    try {
      logger.log('[ProductEditModal] Chargement catégories avec tenant:', tenant.id);
      const data = await api.getCategories();
      // L'API retourne directement un tableau, pas { data: [...] }
      const categoriesArray = Array.isArray(data) ? data : (data.data || []);
      logger.log('[ProductEditModal] Catégories chargées:', categoriesArray.length);
      setCategories(categoriesArray);
    } catch (error: any) {
      logger.error('Erreur chargement catégories:', error);
      logger.error('Détails erreur:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      // Ne pas afficher d'alerte pour les catégories, c'est optionnel
    }
  };

  const loadSuppliers = async () => {
    if (!tenant) return;
    try {
      setSuppliers(await api.getSuppliers(tenant.id));
    } catch (error) {
      logger.error('Erreur chargement fournisseurs:', error);
      // Non bloquant : le champ reste saisissable librement.
    }
  };

  const loadDepositProducts = async () => {
    if (!tenant) {
      logger.warn('Tenant pas encore chargé, skip loadDepositProducts');
      return;
    }

    try {
      logger.log('[ProductEditModal] Chargement produits consigne avec tenant:', tenant.id);
      // Charger uniquement les produits de type consigne (is_deposit = true) via filtre serveur
      const response = await api.getProducts({ active: true, is_deposit: true });
      const deposits = response.data || [];
      logger.log('[ProductEditModal] Produits consigne chargés:', deposits.length);
      setDepositProducts(deposits);
    } catch (error: any) {
      logger.error('Erreur chargement produits consigne:', error);
      // Ne pas afficher d'alerte, c'est optionnel
    }
  };

  // === Gestion multi-images (produit existant) ===

  const handleMultiImageUpload = async (file: File, type: 'PRODUCT' | 'LABEL') => {
    if (!product?.id) return;
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image (JPG, PNG, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("L'image ne doit pas dépasser 10MB");
      return;
    }
    try {
      setUploadingPhoto(true);
      const newImage = await api.uploadProductImage(product.id, file, type);
      changedOutsideSaveRef.current = true;
      setProductImages(prev => [...prev, newImage]);
      // image_url est synchronisé côté backend
      if (newImage.position === 0) {
        setPhotoPreview(newImage.url);
        setFormData(prev => ({ ...prev, image_url: newImage.url }));
      }
      logger.log('[ProductEditModal] Image ajoutée:', newImage.id, type);
    } catch (error: any) {
      logger.error('Erreur upload image:', error);
      alert('Erreur upload: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingPhoto(false);
    }
  };

  /**
   * Réduit la photo SUR L'APPAREIL avant l'envoi, à la taille que le serveur
   * garde de toute façon (1920 px, `UploadsService`). Mesuré le 24/09/2026 : un
   * iPhone envoyait ~2 Mo par photo, soit ~8 s de montée sur les ~10 s d'un
   * ajout, pour que le serveur en jette ensuite les trois quarts ; un envoi a
   * même été abandonné en route (nginx 499). Réduite, la photo pèse ~400 Ko.
   * Qualité 0,88 : le serveur recompresse derrière (JPEG 85), inutile d'empiler
   * deux pertes. Échec de décodage (certains HEIC) = fichier d'origine, comme avant.
   */
  const reduireAvantEnvoi = (f: File) => prepareImageForUpload(f, 1920, 0.88, 0);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const brut = e.target.files?.[0];
    if (!brut) return;
    e.target.value = '';
    const file = await reduireAvantEnvoi(brut);

    if (product?.id) {
      // Mode multi-images (produit existant)
      await handleMultiImageUpload(file, 'PRODUCT');
    } else {
      // Mode single-image (nouveau produit, pas encore de productId)
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner une image (JPG, PNG, etc.)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert("L'image source ne doit pas dépasser 10MB");
        return;
      }
      try {
        setUploadingPhoto(true);
        const oldImageUrl = formData.image_url;
        if (oldImageUrl) {
          try { await api.deleteImageByUrl(oldImageUrl); } catch {}
        }
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
        const token = localStorage.getItem('auth_token');
        const response = await axios.post(`${apiBaseUrl}/uploads/image`, formDataUpload, {
          headers: {
            'X-Tenant-ID': tenant?.id || '',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        const uploadedUrl = response.data.url;
        if (uploadedUrl) {
          setPhotoPreview(uploadedUrl);
          setFormData(prev => ({ ...prev, image_url: uploadedUrl }));
        }
      } catch (error: any) {
        logger.error('Erreur upload image:', error);
        alert('Erreur upload: ' + (error.response?.data?.message || error.message));
      } finally {
        setUploadingPhoto(false);
      }
    }
  };

  const handleLabelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !product?.id) return;
    e.target.value = '';
    await handleMultiImageUpload(file, 'LABEL');
  };

  // Détecte si on est sur mobile/tablette (pour basculer entre capture native et webcam)
  const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const handleCameraCapture = (type: 'PRODUCT' | 'LABEL' = 'PRODUCT') => {
    if (!isMobileDevice) {
      // Desktop (Mac mini POS) : ouvrir la webcam
      setWebcamImageType(type);
      setShowWebcamModal(true);
      return;
    }

    // Mobile : capture native via input.capture
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = async (e) => {
      const brut = (e.target as HTMLInputElement).files?.[0];
      if (!brut) return;
      const file = await reduireAvantEnvoi(brut);

      if (product?.id) {
        await handleMultiImageUpload(file, type);
      } else {
        // Nouveau produit — single image
        try {
          setUploadingPhoto(true);
          const oldImageUrl = formData.image_url;
          if (oldImageUrl) {
            try { await api.deleteImageByUrl(oldImageUrl); } catch {}
          }
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
          const token = localStorage.getItem('auth_token');
          const response = await axios.post(`${apiBaseUrl}/uploads/image`, formDataUpload, {
            headers: {
              'X-Tenant-ID': tenant?.id || '',
              ...(token && { 'Authorization': `Bearer ${token}` }),
            },
          });
          const uploadedUrl = response.data.url;
          if (uploadedUrl) {
            setPhotoPreview(uploadedUrl);
            setFormData(prev => ({ ...prev, image_url: uploadedUrl }));
          }
        } catch (error: any) {
          alert('Erreur upload: ' + (error.response?.data?.message || error.message));
        } finally {
          setUploadingPhoto(false);
        }
      }
    };
    input.click();
  };

  const handleWebcamCapture = async (file: File) => {
    if (product?.id) {
      await handleMultiImageUpload(file, webcamImageType);
    } else {
      // Nouveau produit — single image
      try {
        setUploadingPhoto(true);
        const oldImageUrl = formData.image_url;
        if (oldImageUrl) {
          try { await api.deleteImageByUrl(oldImageUrl); } catch {}
        }
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
        const token = localStorage.getItem('auth_token');
        const response = await axios.post(`${apiBaseUrl}/uploads/image`, formDataUpload, {
          headers: {
            'X-Tenant-ID': tenant?.id || '',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        const uploadedUrl = response.data.url;
        if (uploadedUrl) {
          setPhotoPreview(uploadedUrl);
          setFormData(prev => ({ ...prev, image_url: uploadedUrl }));
        }
      } catch (error: any) {
        alert('Erreur upload: ' + (error.response?.data?.message || error.message));
      } finally {
        setUploadingPhoto(false);
      }
    }
  };

  const handleDeleteProductImage = async (imageId: string) => {
    if (!product?.id) return;
    try {
      await api.deleteProductImage(product.id, imageId);
      changedOutsideSaveRef.current = true;
      const updated = productImages.filter(img => img.id !== imageId);
      setProductImages(updated);
      // Mettre à jour le preview si l'image principale a changé
      if (updated.length > 0) {
        setPhotoPreview(updated[0].url);
        setFormData(prev => ({ ...prev, image_url: updated[0].url }));
      } else {
        setPhotoPreview(null);
        setFormData(prev => ({ ...prev, image_url: '' }));
      }
    } catch (error: any) {
      alert('Erreur suppression: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleMoveImage = async (imageId: string, direction: 'up' | 'down') => {
    if (!product?.id) return;
    const idx = productImages.findIndex(img => img.id === imageId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= productImages.length) return;

    const newOrder = [...productImages];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    setProductImages(newOrder);

    try {
      await api.reorderProductImages(product.id, newOrder.map(img => img.id));
      changedOutsideSaveRef.current = true;
      // Sync l'image principale
      setPhotoPreview(newOrder[0].url);
      setFormData(prev => ({ ...prev, image_url: newOrder[0].url }));
    } catch (error: any) {
      logger.error('Erreur réordonnancement:', error);
    }
  };

  const handleRemovePhoto = async () => {
    if (formData.image_url) {
      try {
        await api.deleteImageByUrl(formData.image_url);
      } catch {}
    }
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  /** Recharger la galerie après une transformation (URLs et vignette changent). */
  const reloadProductImages = async () => {
    if (!product?.id) return [] as ProductImage[];
    changedOutsideSaveRef.current = true;
    const images = await api.getProductImages(product.id);
    setProductImages(images);
    if (images.length > 0) {
      setPhotoPreview(images[0].url);
      setFormData(prev => ({ ...prev, image_url: images[0].url }));
    }
    return images;
  };

  /** Rotation immédiate (quart de tour) — renvoie la nouvelle URL pour l'éditeur. */
  const handleRotateImage = async (imageId: string, rotate: number): Promise<string> => {
    if (!product?.id) throw new Error('Produit inconnu');
    const updated = await api.transformProductImage(product.id, imageId, { rotate });
    await reloadProductImages();
    return updated.url;
  };

  /** Recadrage et/ou point blanc — le serveur régénère l'image sous une nouvelle URL. */
  const handleTransformImage = async (
    imageId: string,
    params: {
      crop?: { x: number; y: number; width: number; height: number };
      white_point_at?: { x: number; y: number };
      white_point_strength?: number;
      erase?: Array<{ x: number; y: number; width: number; height: number }>;
      cutout?: boolean;
      cutout_margin?: number;
      cutout_feather?: number;
    },
  ) => {
    if (!product?.id) return;
    await api.transformProductImage(product.id, imageId, params);
    await reloadProductImages();
  };

  /** Requalifier une photo (produit ↔ étiquette) d'un tap sur son badge. */
  const handleToggleImageType = async (img: ProductImage) => {
    if (!product?.id) return;
    const newType = img.type === 'LABEL' ? 'PRODUCT' : 'LABEL';
    try {
      await api.updateProductImage(product.id, img.id, { type: newType });
      changedOutsideSaveRef.current = true;
      setProductImages(prev => prev.map(i => (i.id === img.id ? { ...i, type: newType } : i)));
    } catch (error: any) {
      alert('Erreur : ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCertifierChange = (value: string) => {
    if (value === 'custom') {
      setShowCustomCertifier(true);
      setFormData({ ...formData, organic_certifier: customCertifier });
    } else {
      setShowCustomCertifier(false);
      setFormData({ ...formData, organic_certifier: value });
    }
  };

  const sanitizeProductData = (data: typeof formData) => {
    // Créer une copie propre des données
    const sanitized: any = {};

    // Champs string - trim et convertir vide en undefined
    const stringFields = ['name', 'code', 'barcode', 'description', 'unit', 'tax_class', 'supplier_name', 'producer_name', 'origin_country', 'organic_certifier'];
    stringFields.forEach(field => {
      const value = (data as any)[field];
      if (value && typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          sanitized[field] = trimmed;
        }
      }
    });

    // Image URL - permettre base64 ou suppression explicite
    if (data.image_url && data.image_url.trim()) {
      sanitized.image_url = data.image_url.trim();
    } else {
      sanitized.image_url = null; // Suppression explicite pour que Prisma mette à jour
    }

    // Category ID - UUID ou vide
    if (data.category_id && data.category_id.trim()) {
      sanitized.category_id = data.category_id.trim();
    }

    // Deposit product ID - UUID ou null (pour retirer une consigne)
    if (data.deposit_product_id && data.deposit_product_id.trim()) {
      sanitized.deposit_product_id = data.deposit_product_id.trim();
    } else {
      sanitized.deposit_product_id = null;
    }

    // Deposit quantity - string décimal
    if (data.deposit_qty_per_unit) {
      sanitized.deposit_qty_per_unit = data.deposit_qty_per_unit.toString();
    }

    // Booléens
    sanitized.is_weighted = Boolean(data.is_weighted);
    sanitized.active = Boolean(data.active);
    sanitized.bio = Boolean(data.bio);
    sanitized.vegan = Boolean(data.vegan);
    sanitized.print_dlc_on_ticket = Boolean(data.print_dlc_on_ticket);
    sanitized.ne_se_coupe_pas = Boolean(data.ne_se_coupe_pas);
    sanitized.is_deposit = Boolean(data.is_deposit);
    sanitized.variable_weight = Boolean(data.variable_weight);
    sanitized.sell_by_pieces = Boolean(data.sell_by_pieces);
    sanitized.ecom_pieces_only = Boolean(data.ecom_pieces_only);
    sanitized.ecom_pieces_first = Boolean(data.ecom_pieces_first);

    // Poids brut (entier positif ou null)
    if (data.gross_weight_grams !== undefined && data.gross_weight_grams !== null) {
      const weightNum = Number(data.gross_weight_grams);
      if (!isNaN(weightNum) && weightNum > 0) {
        sanitized.gross_weight_grams = Math.round(weightNum);
      }
    }

    // Poids moyen par pièce (entier positif ou null)
    if (data.avg_weight_per_piece_grams !== undefined && data.avg_weight_per_piece_grams !== null) {
      const avgNum = Number(data.avg_weight_per_piece_grams);
      if (!isNaN(avgNum) && avgNum > 0) {
        sanitized.avg_weight_per_piece_grams = Math.round(avgNum);
      }
    } else {
      sanitized.avg_weight_per_piece_grams = null;
    }

    // Pack size (conditionnement POS — entier > 1 ou null)
    if (data.pack_size !== undefined && data.pack_size !== null) {
      const packNum = Number(data.pack_size);
      if (!isNaN(packNum) && packNum > 1) {
        sanitized.pack_size = Math.round(packNum);
      } else {
        sanitized.pack_size = null;
      }
    } else {
      sanitized.pack_size = null;
    }

    // Stock quantity - nombre ou undefined
    if (data.stock_quantity !== undefined && data.stock_quantity !== null) {
      const stockNum = Number(data.stock_quantity);
      if (!isNaN(stockNum) && stockNum >= 0) {
        sanitized.stock_quantity = stockNum;
      }
    }

    // Prix (en centimes) - entier ou undefined
    if (data.purchase_price_cents !== undefined && data.purchase_price_cents !== null) {
      const priceNum = Number(data.purchase_price_cents);
      if (!isNaN(priceNum) && priceNum >= 0) {
        sanitized.purchase_price_cents = Math.round(priceNum);
      }
    }

    if (data.suggested_price_cents !== undefined && data.suggested_price_cents !== null) {
      const priceNum = Number(data.suggested_price_cents);
      if (!isNaN(priceNum) && priceNum >= 0) {
        sanitized.suggested_price_cents = Math.round(priceNum);
      }
    }

    // Date d'expiration - format ISO ou null
    if (data.expiry_date && data.expiry_date.trim()) {
      try {
        // Vérifier que c'est une date valide
        const dateStr = data.expiry_date.trim();
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          // Format ISO pour l'API
          sanitized.expiry_date = dateStr;
        }
      } catch (e) {
        logger.warn('Date invalide ignorée:', data.expiry_date);
      }
    }

    // Précommandes
    sanitized.preorder_enabled = Boolean(data.preorder_enabled);
    sanitized.preorder_type_id = data.preorder_type_id || null;
    // Jours de livraison propres à ce produit : vide = ceux du type.
    sanitized.production_days = Array.isArray(data.production_days) ? data.production_days : [];
    sanitized.delivery_weeks_of_month = Array.isArray(data.delivery_weeks_of_month) ? data.delivery_weeks_of_month : [];
    if (data.availability_days && Array.isArray(data.availability_days)) {
      sanitized.availability_days = data.availability_days;
    } else {
      sanitized.availability_days = [];
    }

    // Seuils d'alerte de réapprovisionnement
    if (data.restock_alert_threshold !== null && data.restock_alert_threshold !== undefined) {
      const threshold = Number(data.restock_alert_threshold);
      if (!isNaN(threshold) && threshold >= 0) {
        sanitized.restock_alert_threshold = threshold;
      }
    }
    if (data.order_threshold !== null && data.order_threshold !== undefined) {
      const threshold = Number(data.order_threshold);
      if (!isNaN(threshold) && threshold >= 0) {
        sanitized.order_threshold = threshold;
      }
    }

    return sanitized;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Le nom du produit est requis');
      return;
    }

    try {
      setLoading(true);

      // Nettoyer et valider les données
      const cleanData = sanitizeProductData(formData);

      // Ajouter les prix d'achat et conseillé depuis les états séparés
      if (purchasePrice && purchasePrice.trim()) {
        const purchasePriceCents = Math.round(parseFloat(purchasePrice) * 100);
        if (!isNaN(purchasePriceCents) && purchasePriceCents >= 0) {
          cleanData.purchase_price_cents = purchasePriceCents;
        }
      }
      if (suggestedPrice && suggestedPrice.trim()) {
        const suggestedPriceCents = Math.round(parseFloat(suggestedPrice) * 100);
        if (!isNaN(suggestedPriceCents) && suggestedPriceCents >= 0) {
          cleanData.suggested_price_cents = suggestedPriceCents;
        }
      }

      console.log('[ProductEditModal] purchasePrice state:', purchasePrice);
      console.log('[ProductEditModal] suggestedPrice state:', suggestedPrice);
      console.log('[ProductEditModal] purchase_price_cents in cleanData:', cleanData.purchase_price_cents);
      console.log('[ProductEditModal] suggested_price_cents in cleanData:', cleanData.suggested_price_cents);
      console.log('[ProductEditModal] Données nettoyées:', JSON.stringify(cleanData, null, 2));

      // Déterminer l'ID du produit à mettre à jour
      const productToUpdate = product || selectedExistingProduct;

      if (productToUpdate?.id) {
        // Mise à jour — ne pas renvoyer stock_quantity sauf modification manuelle
        // (sinon le total backroom+display est réécrit en backroom, doublant le stock)
        if (!stockDirty) {
          delete cleanData.stock_quantity;
        }

        // DLC — idem : jamais renvoyée si l'utilisateur ne l'a pas saisie dans CE
        // formulaire. Quand le produit a un lot, la DLC appartient au lot et se
        // modifie via son propre champ (posApi.updateLot).
        if (!expiryDirty) {
          delete cleanData.expiry_date;
        }

        // Prix d'achat / conseillé — idem : jamais renvoyés sans saisie dans CE formulaire.
        if (!priceDirty) {
          delete cleanData.purchase_price_cents;
          delete cleanData.suggested_price_cents;
        }

        // Consigne — ne renvoyer deposit_product_id / deposit_qty_per_unit QUE s'ils
        // ont réellement changé par rapport à la fiche chargée. Le sanitize envoie
        // toujours deposit_product_id (valeur ou null) : si la fiche avait été ouverte
        // avec un objet produit incomplet (champ absent → sanitize l'aurait mis à null),
        // un simple enregistrement effacerait silencieusement la consigne. En ne
        // l'envoyant que s'il diffère, Prisma ne touche pas au champ absent → la consigne
        // est préservée. Le retrait volontaire (valeur → vide) reste envoyé, donc appliqué.
        const originalDepositId = ((productToUpdate as any).deposit_product_id ?? null) || null;
        if (((cleanData.deposit_product_id ?? null) || null) === originalDepositId) {
          delete cleanData.deposit_product_id;
        }
        const originalDepositQty = (productToUpdate as any).deposit_qty_per_unit;
        if (
          cleanData.deposit_qty_per_unit !== undefined &&
          originalDepositQty != null &&
          Number(cleanData.deposit_qty_per_unit) === Number(originalDepositQty)
        ) {
          delete cleanData.deposit_qty_per_unit;
        }

        logger.log('[ProductEditModal] Mise à jour produit:', productToUpdate.id, stockDirty ? '(stock modifié manuellement)' : '(stock inchangé, non envoyé)', expiryDirty ? '(DLC saisie)' : '(DLC non touchée, non envoyée)');
        await api.updateProduct(productToUpdate.id, cleanData);

        // Mise à jour du prix si changé
        const currentPrice = productToUpdate.prices?.[0]?.amount_cents || 0;
        const newPriceCents = Math.round(price * 100);
        if (newPriceCents !== currentPrice) {
          logger.log('[ProductEditModal] Mise à jour prix:', newPriceCents);
          await api.updateProductPrice(productToUpdate.id, newPriceCents);
        }
      } else {
        // Création
        logger.log('[ProductEditModal] Création produit');
        const newProduct = await api.createProduct(cleanData);

        // Définir le prix
        if (price > 0) {
          logger.log('[ProductEditModal] Définition prix:', Math.round(price * 100));
          await api.updateProductPrice(newProduct.id, Math.round(price * 100));
        }
      }

      const isUpdate = product || selectedExistingProduct;
      alert(isUpdate ? 'Produit mis à jour avec succès' : 'Produit créé avec succès');
      onSave();
      onClose();
    } catch (error: any) {
      logger.error('[ProductEditModal] Erreur sauvegarde produit:', error);
      logger.error('[ProductEditModal] Détails erreur:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        data: formData,
      });

      let errorMessage = 'Erreur lors de la sauvegarde du produit';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg shadow-xl w-[95vw] max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selectedExistingProduct && !product && (
              <button
                type="button"
                onClick={handleBackToCreate}
                className="text-gray-500 hover:text-gray-700"
                title="Retour à la création"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            )}
            <h2 className="text-xl font-semibold text-gray-900">
              {product ? 'Modifier le produit' : selectedExistingProduct ? `Modifier: ${selectedExistingProduct.name}` : 'Nouveau produit'}
            </h2>
          </div>
          <button
            onClick={handleCloseModal}
            className="text-gray-400 hover:text-gray-500"
            type="button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Barre de recherche produit existant - Mode création uniquement */}
        {!product && !selectedExistingProduct && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-200">
            <div className="relative">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un produit existant..."
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
                {isSearching && (
                  <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                )}
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Tapez pour rechercher un produit existant et modifier son stock
              </p>

              {/* Résultats de recherche */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                  {searchResults.map((result) => (
                    <button
                      key={result.id}
                      type="button"
                      onClick={() => handleSelectExistingProduct(result.id)}
                      className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{result.name}</div>
                        <div className="text-xs text-gray-500 flex gap-2">
                          {result.code && <span>Code: {result.code}</span>}
                          {result.barcode && <span>EAN: {result.barcode}</span>}
                          {result.category_name && <span className="text-blue-600">{result.category_name}</span>}
                          {result.bio && <span className="text-green-600">Bio</span>}
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form - Layout paysage iPad (4 colonnes) */}
        <form onSubmit={handleSubmit} className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* COLONNE 1 - Photo + Infos de base */}
            <div className="space-y-4">
              {/* Photos du produit (multi-images) */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 mb-2">
                  Photos {product?.id && `(${productImages.length}/5)`}
                </div>

                {/* Grille d'images */}
                {product?.id && productImages.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3 mb-3 pt-1 pr-1">
                    {productImages.map((img, idx) => (
                      <div key={img.id} className="relative group overflow-visible">
                        <img
                          src={img.thumbnail_url || img.url}
                          alt={img.alt_text || `Photo ${idx + 1}`}
                          className="w-full aspect-square object-cover rounded-lg border border-gray-200 cursor-zoom-in"
                          onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (!target.dataset.fallback) {
                              target.dataset.fallback = '1';
                              target.src = img.url;
                            } else {
                              target.style.display = 'none';
                              target.parentElement?.classList.add('bg-gray-200', 'flex', 'items-center', 'justify-center');
                              const icon = document.createElement('span');
                              icon.textContent = '404';
                              icon.className = 'text-xs text-gray-400 font-medium';
                              target.parentElement?.appendChild(icon);
                            }
                          }}
                        />
                        {/* Badge position */}
                        <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                          {idx + 1}
                        </span>
                        {/* Badge type — cliquable pour requalifier (produit ↔ étiquette) */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleToggleImageType(img); }}
                          title={img.type === 'LABEL' ? 'Marquer comme photo produit' : 'Marquer comme étiquette'}
                          className={`absolute top-1 right-6 text-[9px] px-1 rounded text-white ${
                            img.type === 'LABEL' ? 'bg-amber-500' : 'bg-black/40'
                          }`}
                        >
                          {img.type === 'LABEL' ? 'ETQ' : '🏷'}
                        </button>
                        {/* Bouton supprimer */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleDeleteProductImage(img.id); }}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 z-10"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        {/* Éditer (rotation/recadrage) + flèches de réordonnancement */}
                        <div className="absolute bottom-1 left-1 right-1 flex justify-center gap-1">
                          <button type="button" onClick={(e) => { e.stopPropagation(); setEditingImage(img); }}
                            title="Pivoter / recadrer"
                            className="bg-black/60 text-white rounded p-0.5 text-[10px] px-1">
                            ✏️
                          </button>
                          {idx > 0 && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); handleMoveImage(img.id, 'up'); }}
                              className="bg-black/60 text-white rounded p-0.5 text-[10px]">
                              &#9664;
                            </button>
                          )}
                          {idx < productImages.length - 1 && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); handleMoveImage(img.id, 'down'); }}
                              className="bg-black/60 text-white rounded p-0.5 text-[10px]">
                              &#9654;
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !product?.id && photoPreview ? (
                  <div className="flex justify-center mb-3">
                    <div className="relative">
                      <img src={photoPreview} alt="Aperçu" className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300" />
                      <button type="button" onClick={handleRemovePhoto}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : !product?.id ? (
                  <div className="w-full h-28 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center mb-3">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                ) : product?.id && formData.image_url ? (
                  <div className="flex justify-center mb-3">
                    <div className="relative">
                      <img
                        src={formData.image_url}
                        alt="Photo produit"
                        className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300 cursor-zoom-in"
                        onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement?.classList.add('bg-gray-200', 'flex', 'items-center', 'justify-center', 'w-32', 'h-32', 'rounded-lg');
                          const icon = document.createElement('span');
                          icon.textContent = '404';
                          icon.className = 'text-xs text-gray-400 font-medium';
                          target.parentElement?.appendChild(icon);
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemovePhoto(); }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 z-10"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-xs text-gray-400 mb-3 py-4">Aucune photo</div>
                )}

                {/* Boutons d'upload */}
                {uploadingPhoto ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600 py-2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                    Upload...
                  </div>
                ) : (
                  /* 2 boutons au lieu de 4 : un par TYPE de photo. Sur téléphone,
                     l'input natif propose déjà caméra OU galerie (pas besoin d'un
                     bouton caméra séparé) ; sur desktop le bouton ouvre la webcam
                     et le petit 📁 prend un fichier (import AirDrop). Une photo mal
                     typée se requalifie d'un tap sur son badge. */
                  <div className="space-y-2">
                    <input type="file" id="file-upload" accept="image/*" onChange={handleFileUpload} className="hidden" />
                    <input type="file" id="label-upload" accept="image/*" onChange={handleLabelUpload} className="hidden" />
                    <div className="flex gap-1.5 justify-center items-stretch">
                      {isMobileDevice ? (
                        <label htmlFor="file-upload"
                          className={`px-3 py-2 text-white text-xs rounded cursor-pointer flex items-center gap-1.5 ${
                            product?.id && productImages.length >= 5
                              ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                          }`}>
                          📷 Photo produit
                        </label>
                      ) : (
                        <>
                          <button type="button" onClick={() => handleCameraCapture('PRODUCT')}
                            disabled={product?.id ? productImages.length >= 5 : false}
                            className="px-3 py-2 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1.5">
                            📷 Photo produit
                          </button>
                          <label htmlFor="file-upload" title="Depuis un fichier"
                            className={`px-2 py-2 text-white text-xs rounded cursor-pointer flex items-center ${
                              product?.id && productImages.length >= 5
                                ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-700/70 hover:bg-green-700'
                            }`}>
                            📁
                          </label>
                        </>
                      )}
                      {product?.id && (isMobileDevice ? (
                        <label htmlFor="label-upload"
                          className={`px-3 py-2 text-white text-xs rounded cursor-pointer flex items-center gap-1.5 ${
                            productImages.length >= 5
                              ? 'bg-gray-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700'
                          }`}>
                          🏷 Étiquette
                        </label>
                      ) : (
                        <>
                          <button type="button" onClick={() => handleCameraCapture('LABEL')}
                            disabled={productImages.length >= 5}
                            className="px-3 py-2 bg-amber-600 text-white text-xs rounded hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1.5">
                            🏷 Étiquette
                          </button>
                          <label htmlFor="label-upload" title="Depuis un fichier"
                            className={`px-2 py-2 text-white text-xs rounded cursor-pointer flex items-center ${
                              productImages.length >= 5
                                ? 'bg-gray-400 cursor-not-allowed' : 'bg-amber-700/70 hover:bg-amber-700'
                            }`}>
                            📁
                          </label>
                        </>
                      ))}
                    </div>
                  </div>
                )}

                {/* Texte OCR étiquette (dépliable) */}
                {product?.id && productImages.some(img => img.type === 'LABEL' && img.label_text) && (
                  <div className="mt-3 border-t pt-2">
                    <button type="button"
                      onClick={() => setExpandedLabelText(expandedLabelText ? null : productImages.find(img => img.label_text)?.label_text || null)}
                      className="text-xs text-amber-700 hover:text-amber-800 font-medium flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      {expandedLabelText ? 'Masquer' : 'Voir'} le texte OCR
                    </button>
                    {expandedLabelText && (
                      <div className="mt-2">
                        <pre className="text-[10px] text-gray-700 bg-white rounded p-2 whitespace-pre-wrap max-h-40 overflow-y-auto border">{expandedLabelText}</pre>
                        <div className="flex gap-2 mt-1">
                          <button type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, description: expandedLabelText }));
                              alert('Description mise à jour avec le texte de l\'étiquette');
                            }}
                            className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700">
                            Remplacer la description
                          </button>
                          <button type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                description: (prev.description ? prev.description + '\n\n' : '') + expandedLabelText,
                              }));
                              alert('Texte ajouté à la description');
                            }}
                            className="text-[10px] bg-gray-500 text-white px-2 py-0.5 rounded hover:bg-gray-600">
                            Ajouter à la description
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Infos de base */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom du produit"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="TOM-001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">EAN</label>
                    <input
                      type="text"
                      value={formData.barcode}
                      onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="3760..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Aucune</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.path || cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Description..."
                  />
                </div>

                {/* Labels */}
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.vegan}
                      onChange={(e) => setFormData({ ...formData, vegan: e.target.checked })}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Vegan</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Actif</span>
                  </label>
                  <label
                    className="flex items-center"
                    title="Affiche la DLC du lot en cours de vente au panier et l'imprime sur le ticket client (obligation d'information pour les œufs vendus en vrac)"
                  >
                    <input
                      type="checkbox"
                      checked={formData.print_dlc_on_ticket}
                      onChange={(e) => setFormData({ ...formData, print_dlc_on_ticket: e.target.checked })}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">DLC sur ticket</span>
                  </label>
                </div>
              </div>
            </div>

            {/* COLONNE CENTRALE - Prix, Stock, Mise en rayon */}
            <div className="space-y-4">
              {/* Stock en rayon - Section mise en évidence */}
              {(product || selectedExistingProduct) && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Mise en rayon
                    <button
                      type="button"
                      onClick={() => setShowCount(true)}
                      disabled={lotsIllisibles}
                      className="ml-auto px-2.5 py-1 text-xs font-semibold border border-blue-400 text-blue-700 rounded-lg active:bg-blue-100 disabled:opacity-40"
                      title="Corriger le stock : ajustement tracé lot par lot, avec motif (le stock ne s'édite plus directement sur la fiche)"
                    >
                      🔢 Corriger le stock
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRemiseLots(true)}
                      className="px-2.5 py-1 text-xs font-semibold border border-red-400 text-red-700 rounded-lg active:bg-red-50"
                      title="Remiser un lot proche de sa date sans toucher aux autres lots"
                    >
                      🏷️ Anti-gaspi
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSeries(true)}
                      className="px-2.5 py-1 text-xs font-semibold border border-slate-400 text-slate-700 rounded-lg active:bg-slate-100"
                      title="Ce qu'ont donné les dernières livraisons : vendu, perdu, prélevé, marge"
                    >
                      📊 Séries
                    </button>
                  </h4>

                  {lotsIllisibles && (
                    <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                      ⚠️ <strong>Stock illisible</strong> — la lecture des lots a été refusée (session expirée ?).
                      Les chiffres ci-dessous ne sont PAS le stock réel : reconnectez-vous avant de corriger quoi que ce soit.
                    </div>
                  )}
                  {/* Résumé stock total */}
                  <div className={`grid grid-cols-2 gap-3 mb-3 ${lotsIllisibles ? 'opacity-40' : ''}`}>
                    <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                      <div className="text-xl font-bold text-orange-600">
                        {productLots.reduce((sum, lot) => sum + lot.stock_backroom, 0).toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-600">En réserve</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                      <div className="text-xl font-bold text-green-600">
                        {productLots.reduce((sum, lot) => sum + lot.stock_display, 0).toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-600">En rayon</div>
                    </div>
                  </div>

                  {/* Liste des lots avec mise en rayon / remise en réserve */}
                  {lotsLoading ? (
                    <div className="text-center py-2 text-gray-500 text-sm">Chargement...</div>
                  ) : productLots.length === 0 ? (
                    /* Plus rien en stock : on montre quand meme le DERNIER LOT
                       RECU. Sans lui, la DLC, la date de reception, le
                       fournisseur et le numero de lot fournisseur disparaissent
                       de la fiche des que le produit est epuise — et c'est
                       precisement la tracabilite qu'un controle reclame. */
                    <div className="py-2">
                      <div className="text-center text-gray-500 text-sm mb-2">{lotsIllisibles ? 'Lots non lus' : 'Aucun lot en stock'}</div>
                      {dernierLot && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                          <p className="font-semibold text-gray-700 mb-1.5">
                            Dernier lot reçu{' '}
                            <span className="font-normal text-gray-500">(épuisé)</span>
                          </p>
                          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-gray-700">
                            <dt className="text-gray-500">Reçu le</dt>
                            <dd className="font-medium">
                              {dernierLot.reception_date
                                ? new Date(dernierLot.reception_date).toLocaleDateString('fr-BE')
                                : '—'}
                            </dd>
                            <dt className="text-gray-500">DLC</dt>
                            <dd className="font-medium">
                              {dernierLot.expiry_date
                                ? new Date(dernierLot.expiry_date).toLocaleDateString('fr-BE')
                                : '—'}
                            </dd>
                            <dt className="text-gray-500">Lot</dt>
                            <dd className="font-medium">{dernierLot.lot_number || '—'}</dd>
                            <dt className="text-gray-500">Fournisseur</dt>
                            <dd className="font-medium">{dernierLot.supplier_name || '—'}</dd>
                            {dernierLot.supplier_lot_number && (
                              <>
                                <dt className="text-gray-500">N° lot fournisseur</dt>
                                <dd className="font-medium">{dernierLot.supplier_lot_number}</dd>
                              </>
                            )}
                            {typeof dernierLot.purchase_price_cents === 'number' &&
                              dernierLot.purchase_price_cents > 0 && (
                                <>
                                  <dt className="text-gray-500">Prix d'achat</dt>
                                  <dd className="font-medium">
                                    {(dernierLot.purchase_price_cents / 100).toFixed(2)} €
                                  </dd>
                                </>
                              )}
                          </dl>
                          {dernierLot.label_photo_url && (
                            <a
                              href={dernierLot.label_photo_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-2 text-xs text-blue-700 underline"
                            >
                              Voir la photo de l'étiquette
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {productLots.map((lot) => (
                        <div key={lot.id} className="bg-white rounded-lg p-2 shadow-sm">
                          <div className="text-xs font-medium text-gray-900 truncate mb-1 flex items-center justify-between">
                            {/* Clic sur le lot → édition de SA DLC (un lot sans DLC
                                affiche « DLC — » pour rester cliquable lui aussi) */}
                            <button
                              type="button"
                              onClick={() =>
                                setDlcEditLotId(dlcEditLotId === lot.id ? null : lot.id)
                              }
                              className="text-left truncate hover:text-blue-700"
                              title="Modifier la DLC de ce lot"
                            >
                              Lot {lot.lot_number}
                              <span className={`ml-1 font-normal ${lot.expiry_date ? 'text-orange-600' : 'text-gray-400'}`}>
                                · DLC {lot.expiry_date ? new Date(lot.expiry_date).toLocaleDateString('fr-FR') : '—'}
                              </span>
                              <span className="ml-1 text-gray-400">✎</span>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSplitTarget(lot);
                                setSplitQtyBackroom(''); setSplitQtyDisplay(''); setSplitDlc('');
                              }}
                              className="ml-auto mr-2 px-1.5 py-0.5 text-[11px] rounded border border-indigo-300 bg-indigo-50 text-indigo-700 whitespace-nowrap"
                              title="Deux DLC dans ce lot ? Créez le second."
                            >
                              ✂️ Scinder
                            </button>
                            {lot.source_document_url && (
                              <a
                                href={lot.source_document_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 ml-2"
                                title="Voir document source (AFSCA)"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </a>
                            )}
                          </div>

                          {/* Éditeur DLC du lot — enregistré dès qu'une date complète
                              est saisie, comme le champ DLC principal de la fiche */}
                          {dlcEditLotId === lot.id && (
                            <div className="flex items-center gap-2 mb-2">
                              <DLCInput
                                value={lot.expiry_date ? new Date(lot.expiry_date).toISOString().split('T')[0] : ''}
                                onChange={(isoDate) => void handleLotDlcChange(lot.id, isoDate)}
                                compact
                                className="flex-1"
                              />
                              {dlcSavingLotId === lot.id ? (
                                <span className="text-xs text-gray-500 whitespace-nowrap">Enregistrement…</span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setDlcEditLotId(null)}
                                  className="px-2 py-1 text-xs text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
                                >
                                  Fermer
                                </button>
                              )}
                            </div>
                          )}

                          {/* Ligne réserve → rayon */}
                          {lot.stock_backroom > 0 && (
                            <div className="flex items-center justify-between gap-1 mb-1">
                              <div className="text-xs text-orange-600 w-16">
                                Rés: {lot.stock_backroom.toFixed(1)}
                              </div>
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  inputMode={formData.is_weighted || formData.unit === 'kg' ? 'decimal' : 'numeric'}
                                  value={moveQuantities[lot.id] ?? lot.stock_backroom}
                                  onChange={(e) => setMoveQuantities({
                                    ...moveQuantities,
                                    [lot.id]: Math.min(parseFloat(e.target.value.replace(',', '.')) || 0, lot.stock_backroom)
                                  })}
                                  className="w-14 px-1 py-0.5 text-xs border border-gray-300 rounded text-center"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleMoveToDisplay(lot.id)}
                                  disabled={movingLotId === lot.id}
                                  className="px-2 py-0.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
                                >
                                  {movingLotId === lot.id ? '...' : '→ Rayon'}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Ligne rayon → réserve */}
                          {lot.stock_display > 0 && (
                            <div className="flex items-center justify-between gap-1">
                              <div className="text-xs text-green-600 w-16">
                                Ray: {lot.stock_display.toFixed(1)}
                              </div>
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  inputMode={formData.is_weighted || formData.unit === 'kg' ? 'decimal' : 'numeric'}
                                  value={backroomQuantities[lot.id] ?? lot.stock_display}
                                  onChange={(e) => setBackroomQuantities({
                                    ...backroomQuantities,
                                    [lot.id]: Math.min(parseFloat(e.target.value.replace(',', '.')) || 0, lot.stock_display)
                                  })}
                                  className="w-14 px-1 py-0.5 text-xs border border-gray-300 rounded text-center"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleMoveToBackroom(lot.id)}
                                  disabled={movingLotId === lot.id}
                                  className="px-2 py-0.5 bg-orange-500 text-white text-xs rounded hover:bg-orange-600 disabled:opacity-50 whitespace-nowrap"
                                >
                                  {movingLotId === lot.id ? '...' : '← Rés.'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Stock total */}
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Stock total</span>
                      <span className="text-sm font-bold text-gray-900">
                        {productLots.reduce((sum, lot) => sum + lot.stock_backroom + lot.stock_display, 0).toFixed(1)} {formData.unit}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Prix et TVA */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Prix et TVA</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Prix TTC</label>
                    <PriceInput
                      valueEuros={price}
                      onChangeEuros={(euros) => setPrice(euros)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">TVA</label>
                    <select
                      value={formData.tax_class}
                      onChange={(e) => setFormData({ ...formData, tax_class: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="0%">0% (Vidange/Exonéré)</option>
                      <option value="6%">6%</option>
                      <option value="21%">21%</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Unité</label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pièce">Pièce</option>
                      <option value="kg">Kilogramme</option>
                      <option value="g">Gramme</option>
                      <option value="L">Litre</option>
                      <option value="mL">Millilitre</option>
                    </select>
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_weighted}
                        onChange={(e) => setFormData({ ...formData, is_weighted: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Pesé</span>
                    </label>
                  </div>
                </div>
              </div>

              {showRemiseLots && (product || selectedExistingProduct) && (
                <RemiseLotsModal
                  product={(product || selectedExistingProduct)! as any}
                  onClose={() => setShowRemiseLots(false)}
                />
              )}

              {showSeries && (product || selectedExistingProduct) && (
                <BilanSeriesModal
                  product={(product || selectedExistingProduct)! as any}
                  onClose={() => setShowSeries(false)}
                />
              )}

              {showCount && (product || selectedExistingProduct) && (
                <ProductCountModal
                  product={(product || selectedExistingProduct)!}
                  onClose={() => setShowCount(false)}
                  onSaved={async () => {
                    // Le comptage a DÉJÀ écrit en base : la liste du parent doit
                    // recharger même si la fiche se ferme sans « Enregistrer ».
                    changedOutsideSaveRef.current = true;
                    const id = (product || selectedExistingProduct)?.id;
                    if (id) {
                      try {
                        setProductLots(withStock(await posApi.getProductLots(id)));
                      } catch { /* noop */ }
                    }
                  }}
                />
              )}

              {/* Section "Stock (ajustement manuel)" supprimée le 01/06/2026 :
                  source de bugs car écrasait silencieusement lot.stock_backroom (cf. Bug Mojito).
                  Les ajustements de stock passent désormais par : Mise en rayon, Sortie de stock,
                  Inventaire, ou bouton "Ajuster" sur la fiche du lot (audit trail garanti). */}
            </div>

            {/* COLONNE 3 - Poids & Balance, Marge, Traçabilité */}
            <div className="space-y-4">
              {/* Poids & Balance */}
              <div className={`rounded-lg p-4 border-2 ${scaleConnected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l3 9a5.002 5.002 0 01-6.001 0M18 7l-3 9m-5.257-9L12 5m0 0l2.257.643M12 5v14" />
                    </svg>
                    Poids & Balance
                  </span>
                  {scaleConnected ? (
                    <button
                      type="button"
                      onClick={() => scaleDisconnect()}
                      className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1"
                    >
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                      Connectée — Déconnecter
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => scaleConnect()}
                      disabled={scaleConnecting}
                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      {scaleConnecting ? (
                        <>
                          <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                          Connexion...
                        </>
                      ) : (
                        <>
                          <span className="inline-block w-2 h-2 rounded-full bg-red-400"></span>
                          Connecter la balance
                        </>
                      )}
                    </button>
                  )}
                </h4>

                {scaleConnected && (
                  <div className="bg-white rounded-lg p-3 mb-3 text-center border border-green-200">
                    <div className="text-3xl font-bold font-mono text-green-800">
                      {(scaleWeightGrams / 1000).toFixed(3)} kg
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      {scaleWeightGrams} g — {scaleWeight?.stable ? 'Stable' : 'Lecture...'}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Poids brut de référence (g)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={formData.gross_weight_grams || ''}
                        onChange={(e) => setFormData({ ...formData, gross_weight_grams: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-semibold"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (scaleWeightGrams > 0) {
                            setFormData({ ...formData, gross_weight_grams: Math.round(scaleWeightGrams) });
                          }
                        }}
                        disabled={!scaleConnected || scaleWeightGrams <= 0}
                        className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 whitespace-nowrap ${
                          scaleConnected && scaleWeightGrams > 0
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                        title={scaleConnected ? `Capturer ${scaleWeightGrams}g` : 'Connectez la balance'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l3 9a5.002 5.002 0 01-6.001 0M18 7l-3 9m-5.257-9L12 5m0 0l2.257.643M12 5v14" />
                        </svg>
                        Peser
                      </button>
                    </div>
                    {formData.gross_weight_grams > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        = {(formData.gross_weight_grams / 1000).toFixed(3)} kg
                      </div>
                    )}
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.variable_weight}
                      onChange={(e) => setFormData({ ...formData, variable_weight: e.target.checked })}
                      className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm text-gray-700">Poids variable</span>
                      <p className="text-xs text-gray-500">Fruits, fromages, vrac — tolérance large calcul panier</p>
                    </div>
                  </label>

                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.sell_by_pieces}
                        onChange={(e) => setFormData({ ...formData, sell_by_pieces: e.target.checked })}
                        className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div>
                        <span className="text-sm text-gray-700">Vendable à la pièce (e-commerce)</span>
                        <p className="text-xs text-gray-500">Carotte, betterave, poireau... Le client choisit le nombre de pièces</p>
                      </div>
                    </label>

                    {formData.sell_by_pieces && (
                      <div className="mt-2 ml-6">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Poids moyen par pièce (g)</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="10"
                            min="10"
                            value={formData.avg_weight_per_piece_grams || ''}
                            onChange={(e) => setFormData({ ...formData, avg_weight_per_piece_grams: parseInt(e.target.value) || null })}
                            placeholder="Ex: 200"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-semibold"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (scaleWeightGrams > 0) {
                                setFormData({ ...formData, avg_weight_per_piece_grams: Math.round(scaleWeightGrams) });
                              }
                            }}
                            disabled={!scaleConnected || scaleWeightGrams <= 0}
                            className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 whitespace-nowrap ${
                              scaleConnected && scaleWeightGrams > 0
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                            title={scaleConnected ? `Capturer ${scaleWeightGrams}g` : 'Connectez la balance'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l3 9a5.002 5.002 0 01-6.001 0M18 7l-3 9m-5.257-9L12 5m0 0l2.257.643M12 5v14" />
                            </svg>
                            Peser
                          </button>
                        </div>
                        {formData.avg_weight_per_piece_grams && formData.avg_weight_per_piece_grams > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            = {(formData.avg_weight_per_piece_grams / 1000).toFixed(3)} kg/pièce
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Exemples : carotte ~200g, betterave ~300g, poireau ~250g, courgette ~300g
                        </p>

                        <label className="flex items-start gap-2 cursor-pointer mt-3">
                          <input
                            type="checkbox"
                            checked={formData.ecom_pieces_only}
                            onChange={(e) => setFormData({ ...formData, ecom_pieces_only: e.target.checked })}
                            className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div>
                            <span className="text-sm text-gray-700">Sur l'e-shop : à la pièce UNIQUEMENT</span>
                            <p className="text-xs text-gray-500">
                              Ananas, pastèque, courge… Le client ne peut plus commander « 250 g ».
                              La caisse continue de peser : le prix se règle au poids réel à la préparation.
                            </p>
                          </div>
                        </label>

                        {!formData.ecom_pieces_only && (
                          <label className="flex items-start gap-2 cursor-pointer mt-3">
                            <input
                              type="checkbox"
                              checked={!!formData.ecom_pieces_first}
                              onChange={(e) => setFormData({ ...formData, ecom_pieces_first: e.target.checked })}
                              className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div>
                              <span className="text-sm text-gray-700">Sur l'e-shop : proposer d'abord à la pièce</span>
                              <p className="text-xs text-gray-500">
                                Mangue, citron, poireau… Sans cette case, le choix s'ouvre au poids
                                (tomates cerises, carottes) ; le client peut toujours changer d'onglet.
                              </p>
                            </div>
                          </label>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <label className="block text-sm text-gray-700 mb-1">Conditionnement POS (pack)</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Si {'>'} 1, le POS proposera +1 ou +X au tap (ex : œufs par 6, bières par 24)
                    </p>
                    <input
                      type="number"
                      step="1"
                      min="2"
                      value={formData.pack_size ?? ''}
                      onChange={(e) => setFormData({ ...formData, pack_size: e.target.value === '' ? null : parseInt(e.target.value) || null })}
                      placeholder="Ex : 6"
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Prix d'achat et marge */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Marge</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Achat HT</label>
                    <PriceInput
                      valueEuros={purchasePrice}
                      onChangeEurosString={(euros) => {
                        setPurchasePrice(euros);
                        setPriceDirty(true);
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Conseillé TTC</label>
                    <PriceInput
                      valueEuros={suggestedPrice}
                      onChangeEurosString={(euros) => {
                        setSuggestedPrice(euros);
                        setPriceDirty(true);
                      }}
                      className="w-full"
                    />
                  </div>
                </div>
                {purchasePrice && parseFloat(purchasePrice) > 0 && price > 0 && (
                  <div className="mt-2 p-2 bg-blue-100 rounded text-center">
                    <span className="text-sm font-bold text-blue-800">
                      Marge: {(((price - parseFloat(purchasePrice)) / parseFloat(purchasePrice)) * 100).toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>

              {/* Traçabilité */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Traçabilité</h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Fournisseur</label>
                      {/* Sélecteur (datalist) sur la liste dédupliquée, comme en réception :
                          le champ libre créait des variantes d'orthographe (« Vajra » à
                          côté de « Vajra / Delibio »). La saisie reste possible pour un
                          nouveau fournisseur ; le backend canonicalise dans tous les cas. */}
                      <input
                        type="text"
                        list="product-suppliers"
                        value={formData.supplier_name}
                        onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Choisir ou saisir…"
                      />
                      <datalist id="product-suppliers">
                        {suppliers.map((s) => (
                          <option key={s} value={s} />
                        ))}
                      </datalist>
                      {supplierSuggestion.suggestion && !supplierSuggestion.exact && (
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({ ...formData, supplier_name: supplierSuggestion.suggestion as string })
                          }
                          className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs hover:bg-amber-100"
                        >
                          💡 Proche de «&nbsp;<strong>{supplierSuggestion.suggestion}</strong>&nbsp;» — utiliser
                        </button>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Producteur</label>
                      <input
                        type="text"
                        value={formData.producer_name}
                        onChange={(e) => setFormData({ ...formData, producer_name: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Jean Martin"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Origine</label>
                      <input
                        type="text"
                        value={formData.origin_country}
                        onChange={(e) => setFormData({ ...formData, origin_country: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Belgique"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">DLC</label>
                      {primaryLot ? (
                        // Le produit a des lots : la DLC appartient aux lots et s'édite
                        // en cliquant un lot dans « Mise en rayon » (06/08/2026). Le
                        // champ éditable qui vivait ici doublonnait ce chemin — même
                        // famille de doublon que l'incident Chips Tortilla du 04/08.
                        // On affiche la prochaine DLC (FEFO) en lecture seule.
                        <div className="px-2 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-md">
                          {primaryLot.expiry_date
                            ? new Date(primaryLot.expiry_date).toLocaleDateString('fr-FR')
                            : '—'}
                          <span className="ml-1 text-xs text-gray-400">
                            (lot {primaryLot.lot_number} — modifiable via la liste des lots)
                          </span>
                        </div>
                      ) : (
                        // Pas de lot : seul endroit où poser une DLC (création, fiche sans stock)
                        <DLCInput
                          value={formData.expiry_date}
                          onChange={(isoDate) => {
                            setExpiryDirty(true);
                            setFormData({ ...formData, expiry_date: isoDate });
                          }}
                          compact
                          className="w-full"
                        />
                      )}
                    </div>
                  </div>
                  {formData.bio && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Certificateur bio</label>
                      {!showCustomCertifier ? (
                        <select
                          value={formData.organic_certifier || ''}
                          onChange={(e) => handleCertifierChange(e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Sélectionner</option>
                          <optgroup label="Belgique">
                            {certifiers.belgium.map((cert) => (
                              <option key={cert} value={cert}>{cert}</option>
                            ))}
                          </optgroup>
                          <optgroup label="France">
                            {certifiers.france.map((cert) => (
                              <option key={cert} value={cert}>{cert}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Italie">
                            {certifiers.italy.map((cert) => (
                              <option key={cert} value={cert}>{cert}</option>
                            ))}
                          </optgroup>
                          <option value="custom">Autre...</option>
                        </select>
                      ) : (
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={customCertifier}
                            onChange={(e) => {
                              setCustomCertifier(e.target.value);
                              setFormData({ ...formData, organic_certifier: e.target.value });
                            }}
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Certificateur"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setShowCustomCertifier(false);
                              setCustomCertifier('');
                              setFormData({ ...formData, organic_certifier: '' });
                            }}
                            className="px-2 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-xs"
                          >
                            ←
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* COLONNE 4 - Alertes, Consignes, Précommandes */}
            <div className="space-y-4">
              {/* Alertes de réapprovisionnement */}
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <h4 className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Alertes de réapprovisionnement
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Seuil rayon bas
                      <span className="text-gray-400 ml-1">(défaut: 3)</span>
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={formData.restock_alert_threshold ?? ''}
                      onChange={(e) => setFormData({ ...formData, restock_alert_threshold: e.target.value === '' ? null : parseInt(e.target.value) })}
                      placeholder="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Alerte si stock rayon ≤ seuil</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Seuil commande
                      <span className="text-gray-400 ml-1">(défaut: 5)</span>
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={formData.order_threshold ?? ''}
                      onChange={(e) => setFormData({ ...formData, order_threshold: e.target.value === '' ? null : parseInt(e.target.value) })}
                      placeholder="5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Suggestion si stock total ≤ seuil</p>
                  </div>
                </div>

                {/* Rayon plein — l'inverse d'un seuil : non pas « préviens-moi
                    quand il n'y en a presque plus », mais « voilà ce qui tient ».
                    Rempli tout seul à partir des mises en rayon ; ce qu'on écrit
                    ici prime définitivement sur l'observation. */}
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Rayon plein
                    <span className="text-gray-400 ml-1">(ce qui tient sur l'étal)</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.shelf_capacity ?? ''}
                    onChange={(e) => {
                      const brut = e.target.value.replace(',', '.');
                      setFormData({
                        ...formData,
                        shelf_capacity: brut === '' ? null : (parseFloat(brut) as number),
                      });
                    }}
                    placeholder="déduit automatiquement"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Déduit des mises en rayon ; ce que vous saisissez n'est plus jamais écrasé.
                  </p>
                </div>
              </div>

              {/* Consignes */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Consignes</h4>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_deposit}
                      onChange={(e) => setFormData({ ...formData, is_deposit: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Ce produit est une consigne</span>
                  </label>

                  {!formData.is_deposit && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Consigne associée</label>
                        <select
                          value={formData.deposit_product_id}
                          onChange={(e) => setFormData({ ...formData, deposit_product_id: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Aucune</option>
                          {depositProducts.map((dep) => (
                            <option key={dep.id} value={dep.id}>
                              {dep.name} - {((dep.prices?.[0]?.amount_cents || 0) / 100).toFixed(2)}€
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Qté/unité</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.deposit_qty_per_unit}
                          onChange={(e) => setFormData({ ...formData, deposit_qty_per_unit: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={!formData.deposit_product_id}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Précommandes */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Précommandes</h4>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.preorder_enabled}
                      onChange={(e) => setFormData({ ...formData, preorder_enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Activer les précommandes</span>
                  </label>

                  {formData.preorder_enabled && (
                    <div className="pl-6 space-y-2">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Type de précommande</label>
                        <select
                          value={formData.preorder_type_id || ''}
                          onChange={(e) => setFormData({ ...formData, preorder_type_id: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="">— Choisir un type —</option>
                          {preorderTypes.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-400 mt-1">
                          Le type définit la cadence (jours de reprise, échéance de commande) et les options de préparation.
                        </p>
                      </div>
                      {(selType?.prep_options || []).includes('coupé') && (
                        // Le type Pain propose « coupé » ; une baguette, une ciabatta,
                        // une brioche ne se coupent pas — ni en ligne ni en caisse.
                        <label className="flex items-center gap-2 pt-1 text-sm text-gray-700 min-h-[48px]">
                          <input
                            type="checkbox"
                            checked={!!formData.ne_se_coupe_pas}
                            onChange={(e) => setFormData({ ...formData, ne_se_coupe_pas: e.target.checked })}
                            className="w-5 h-5"
                          />
                          Ce pain ne se coupe pas (baguette, ciabatta, brioche…)
                        </label>
                      )}
                      {selType && (selType.pickup_days || []).length > 0 && (
                        <div className="text-xs text-gray-500 pt-1">
                          Jours de reprise (selon le type) : {(selType.pickup_days || []).map((d) => WEEKDAYS_SHORT[d]).join(' · ')}
                        </div>
                      )}
                      {/* Surcharge par produit : dans une même gamme, tous les
                          articles ne sont pas livrés les mêmes jours (un pain cuit
                          le vendredi, un autre le samedi). C'est ce qui permet de
                          répartir une commande entre deux jours de préparation. */}
                      {formData.preorder_type_id && (
                        <div className="pt-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Jours de livraison de CE produit
                          </label>
                          <div className="flex gap-1 flex-wrap">
                            {WEEKDAYS_SHORT.map((w, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  const cur = formData.production_days || [];
                                  setFormData({
                                    ...formData,
                                    production_days: cur.includes(i)
                                      ? cur.filter((x) => x !== i)
                                      : [...cur, i].sort(),
                                  });
                                }}
                                className={`px-2 py-1.5 rounded text-xs border-2 min-h-[48px] ${
                                  (formData.production_days || []).includes(i)
                                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                                    : 'border-gray-200 text-gray-500'
                                }`}
                              >
                                {w}
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Laissez vide pour suivre le fournisseur du type. Renseignez
                            si CE produit n'arrive que certains jours — la commande sera
                            calée sur le dernier de ces jours avant la reprise.
                          </p>

                          {/* Livraison une semaine sur deux : le pain de seigle
                              n'arrive que les 1er et 3e vendredis. Sans cette
                              notion, le calcul remonterait jusqu'à une livraison
                              vieille de deux semaines. */}
                          <label className="block text-xs font-medium text-gray-600 mt-3 mb-1">
                            Seulement certaines semaines du mois
                          </label>
                          <div className="flex gap-1 flex-wrap">
                            {[1, 2, 3, 4, 5].map((w) => (
                              <button
                                key={w}
                                type="button"
                                onClick={() => {
                                  const cur = formData.delivery_weeks_of_month || [];
                                  setFormData({
                                    ...formData,
                                    delivery_weeks_of_month: cur.includes(w)
                                      ? cur.filter((x) => x !== w)
                                      : [...cur, w].sort(),
                                  });
                                }}
                                className={`px-2 py-1.5 rounded text-xs border-2 min-h-[48px] ${
                                  (formData.delivery_weeks_of_month || []).includes(w)
                                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                                    : 'border-gray-200 text-gray-500'
                                }`}
                              >
                                {w}{w === 1 ? 're' : 'e'}
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Vide = toutes les semaines. Ex. pain de seigle livré les
                            1er et 3e vendredis : cochez Ven ci-dessus, puis 1re et 3e ici.
                          </p>
                        </div>
                      )}
                      {selType?.pickup_mode === 'SPECIFIC_DAY' && (
                        <>
                          <div className="text-xs text-gray-400 pt-1">Jours e-commerce (pain) :</div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.availability_days.includes('FRIDAY')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                availability_days: [...formData.availability_days, 'FRIDAY'] as ('FRIDAY' | 'SATURDAY')[],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                availability_days: formData.availability_days.filter(d => d !== 'FRIDAY') as ('FRIDAY' | 'SATURDAY')[],
                              });
                            }
                          }}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Vendredi</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.availability_days.includes('SATURDAY')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                availability_days: [...formData.availability_days, 'SATURDAY'] as ('FRIDAY' | 'SATURDAY')[],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                availability_days: formData.availability_days.filter(d => d !== 'SATURDAY') as ('FRIDAY' | 'SATURDAY')[],
                              });
                            }
                          }}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Samedi</span>
                      </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer — sur téléphone, deux rangées : les outils (étiquette,
              annonce) puis Annuler / Enregistrer sur toute la largeur. Sur une
              seule ligne, « Enregistrer » sortait de l'écran (capture du 26/09). */}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-t border-gray-200 pt-4">
            {/* Bouton impression étiquette - uniquement en mode édition */}
            <div className="min-w-0">
              {(product?.id || selectedExistingProduct?.id) && (
                <button
                  type="button"
                  onClick={() => setShowLabelPrintModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Imprimer Étiquette
                </button>
              )}

              {/* Annoncer en nouveauté — chemin de rattrapage, et seul endroit
                  commode pour un « De retour » sur un produit qu'on ne range pas
                  aujourd'hui. L'annonce s'éteint seule à l'ouverture suivante de
                  la boutique : rien à retirer si on oublie. */}
              {(product?.id || selectedExistingProduct?.id) &&
                (nouveaute ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="px-2.5 py-1.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-300 text-sm font-semibold">
                      ✨ {nouveaute.mot} en boutique
                    </span>
                    <button
                      type="button"
                      onClick={() => void retirerNouveaute()}
                      disabled={nouveauteEnCours}
                      className="text-sm text-gray-600 hover:text-gray-900 hover:underline disabled:opacity-40"
                    >
                      retirer
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-500 whitespace-nowrap">Annoncer :</span>
                    <button
                      type="button"
                      onClick={() => void annoncerNouveaute('NEW')}
                      disabled={nouveauteEnCours}
                      className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40"
                    >
                      ✨ Nouveau
                    </button>
                    <button
                      type="button"
                      onClick={() => void annoncerNouveaute('BACK')}
                      disabled={nouveauteEnCours}
                      className="px-3 py-1.5 rounded-md border border-emerald-600 text-emerald-800 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-40"
                    >
                      De retour
                    </button>
                  </div>
                ))}
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
              <button
                type="button"
                onClick={handleCloseModal}
                className="flex-1 sm:flex-none min-h-[48px] px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 sm:flex-none min-h-[48px] px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                )}
                {(product || selectedExistingProduct) ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </form>

        {/* Modal d'impression d'étiquettes */}
        {showLabelPrintModal && (product?.id || selectedExistingProduct?.id) && (
          <PrintShelfLabelsModal
            onClose={() => setShowLabelPrintModal(false)}
            preselectedProductId={product?.id || selectedExistingProduct?.id || ''}
          />
        )}
        <WebcamCaptureModal
          isOpen={showWebcamModal}
          onClose={() => setShowWebcamModal(false)}
          onCapture={handleWebcamCapture}
        />
        {lightboxOpen && (
          <ImageLightbox
            images={
              productImages.length > 0
                ? productImages.map(img => ({ url: img.url, alt: img.alt_text || undefined, label: img.type === 'LABEL' ? 'ETQ' : undefined }))
                : formData.image_url ? [{ url: formData.image_url, alt: 'Photo produit' }] : []
            }
            initialIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
          />
        )}

        {/* Rotation / recadrage / point blanc d'une photo */}
        {editingImage && (
          <ImageEditModal
            imageUrl={editingImage.url}
            onRotate={(rotate) => handleRotateImage(editingImage.id, rotate)}
            onSample={(x, y) => api.sampleProductImageColor(product!.id, editingImage.id, x, y)}
            onCutoutPreview={(margin, feather, shadow, strength) => api.previewProductImageCutout(product!.id, editingImage.id, margin, feather, shadow, strength)}
            onTextZones={() => api.productImageTextZones(product!.id, editingImage.id)}
            onApply={(params) => handleTransformImage(editingImage.id, params)}
            allowErase={editingImage.type !== 'LABEL'}
            onClose={() => setEditingImage(null)}
          />
        )}

        {/* Scission de lot : deux DLC dans une même livraison */}
        {splitTarget && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
              <h3 className="text-lg font-bold text-gray-900">Scinder le lot {splitTarget.lot_number}</h3>
              <p className="mt-1 text-sm text-gray-600">
                À utiliser quand les colis reçus portent <strong>deux DLC différentes</strong>.
                La quantité indiquée passe sur un nouveau lot, qui garde le même fournisseur,
                le même prix et la même traçabilité.
              </p>
              <div className="mt-4 text-xs text-gray-500">
                Disponible — réserve : <strong>{splitTarget.stock_backroom}</strong>
                {' · '}rayon : <strong>{splitTarget.stock_display}</strong>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Quantité en réserve</label>
                  <input type="text" inputMode="decimal" value={splitQtyBackroom}
                    onChange={(e) => setSplitQtyBackroom(e.target.value)} placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Quantité en rayon</label>
                  <input type="text" inputMode="decimal" value={splitQtyDisplay}
                    onChange={(e) => setSplitQtyDisplay(e.target.value)} placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">DLC du nouveau lot</label>
                <DLCInput value={splitDlc} onChange={(iso) => setSplitDlc(iso)} className="w-full" />
              </div>
              <div className="mt-5 flex gap-2">
                <button onClick={() => setSplitTarget(null)}
                  className="flex-1 py-2.5 rounded-lg bg-gray-100 text-gray-700 font-medium">
                  Annuler
                </button>
                <button onClick={confirmSplit} disabled={splitting}
                  className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold disabled:opacity-50">
                  {splitting ? 'Scission…' : 'Créer le second lot'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
